import {
  Banner, type BannerType, Button,
  CheckboxElement,
  FormField,
  FormFieldControl,
  FormFieldError,
  FormFieldMessageContainer,
  ListItem,
  RadioGroup,
  SheetClose,
  SheetContentWrapper, SheetFooter,
  SheetHeader, space, SpaceBetween, TextField, tok,
  Typography,
} from '@sunwebgroup/ui-components';
import { Controller, useForm } from 'react-hook-form';
import * as React from 'react';
import { styled } from 'styled-components';
import { addCar, editCar } from '@/scopes/checkout/domain/actions';
import { createCarEvent } from '@/scopes/checkout/domain/analytics';
import { useService } from '@/scopes/checkout/domain/hooks';
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types';
import { useCarRentalServiceStore, carCouldHaveHigherPrice } from '@/scopes/checkout/components/services/CarRentalFilteringService/store';
import { sub } from 'date-fns';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { useIntl } from '@/intl';

interface DriverFormValues {
  driverId: string;
  driverBirthdate: string;
  hasCreditCard: boolean;
}

const validateMinAge = (value: string) => {
  if (!value) {
    return false;
  }
  const today = new Date();
  const birthDate = new Date(value);
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

export const DriverForm = ({showDriverForm, setShowDriverForm, ...option }: CheckoutCarRentalServiceOption & {showDriverForm: boolean, setShowDriverForm: (value: boolean) => void;  }) => {
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering);
  const [isFixed, setIsFixed] = React.useState(false)
  const intl = useIntl();
  const contentWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const {sendEvent} = useAnalytics();
  const { endEditingCar, setCarToBeSelected,
  } = useCarRentalServiceStore()
  const {availableDrivers, participants, configurations, carLists} = service || {};
  const {driverId, label, value: carValue, id: carId} = option;

  const driverMinBirthdate = sub(new Date(), {years: 99});
  const driverMaxBirthdate = sub(new Date(), {years: configurations?.minAgeDriver, days: 1});
  const defaultDriverBirthdate = sub(new Date(), {years: configurations?.rentalConditionsMinAgeDriver, days: 1});
  const driverMinAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver });
  const driverMaxAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMaxAgeDriver });
  const driver = driverId ? participants?.[driverId] : null;
  const [showWarningDriverAge, setShowWarningDriverAge] = React.useState(useCarRentalServiceStore(carCouldHaveHigherPrice(carId, driverMaxAgeRentalConditions, driverMinAgeRentalConditions)));

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<DriverFormValues>({
    mode: 'onBlur',
    defaultValues: {
      driverId: driverId ?? '',
      driverBirthdate: driver?.birthdate ? new Date(driver.birthdate).toISOString().split('T')[0] : new Date(defaultDriverBirthdate).toISOString().split('T')[0],
      hasCreditCard: Boolean(driverId),
    },
  });

  React.useEffect(() => {
    const checkHeight = () => {
      const contentHeight = contentWrapperRef.current?.scrollHeight || 0;
      const clientHeight = contentWrapperRef.current?.clientHeight || 0;
      setIsFixed(contentHeight > clientHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      checkHeight();
    });

    if (contentWrapperRef.current) {
      resizeObserver.observe(contentWrapperRef.current);
    }

    if (showDriverForm) {
      setTimeout(() => {
        checkHeight();
      }, 0);
    }
    window.addEventListener('resize', checkHeight);

    return () => {
      window.removeEventListener('resize', checkHeight);

      if (contentWrapperRef.current) {
        resizeObserver.unobserve(contentWrapperRef.current);
      }
    };
  }, [setValue, defaultDriverBirthdate, participants, showDriverForm, driver]);


  const handleCloseButton = () => {
    endEditingCar(option.value);
    setShowDriverForm(false);
  }

  const handleDriverChange = (value: string) => {
    setValue('driverId', value);
    const selectedDriver = participants?.[value];

    setValue(
      "driverBirthdate",
      selectedDriver?.birthdate
        ? new Date(selectedDriver.birthdate).toISOString().split("T")[0]
        : new Date(defaultDriverBirthdate).toISOString().split("T")[0]
    );
    setValue("hasCreditCard", selectedDriver?.id ? (driver?.id === selectedDriver?.id) : false);
  }

  const onSubmit = React.useCallback((result: DriverFormValues) => {
   if (!service) return;
    setCarToBeSelected('');
    endEditingCar(carId);
    setShowDriverForm(false);
    const updatedBirthdate = new Date(result.driverBirthdate); // Ensure latest value is used
    if (driver) {
      setServiceValue(editCar({
        value: carValue,
        id: carId,
        participantId: result.driverId ?? '',
        driverBirthDate: updatedBirthdate,
        bookedEquipmentIds: []
      })(service));
      sendEvent(
        createCarEvent({
          result: 'Car edited',
          type: carValue,
          description: 'editCarDriver',
          element: 'button',
          validated: true
        })
      )
    } else {
      setServiceValue(addCar({
        value: carValue,
        id: carId,
        participantId: result.driverId ?? '',
        driverBirthDate: updatedBirthdate,
        bookedEquipmentIds: []
      })(service));
      sendEvent(
        createCarEvent({
          result: 'Car added',
          type: carValue,
          description: 'selectCar',
          element: 'button',
          validated: true
        })
      )
    }

  }, [driver, setShowDriverForm, service, setServiceValue, endEditingCar, option, sendEvent, setCarToBeSelected]);

  if (!service) return null;
  if (!option) return null;
  if (!participants) return null;

  return <>
    <SheetHeader>
      <SheetClose onClick={handleCloseButton}>{intl.formatMessage({id: 'close'})}</SheetClose>
    </SheetHeader>
    <DriverFormWrapper ref={contentWrapperRef}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
          <CarListName $variant="heading-md">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_car_model'}, {carModel: label})} </CarListName>
          <FormField
            $error={
              errors?.driverId
                ? Boolean(errors?.driverId)
                : undefined
            }
          >
            {errors.driverId && (
              <FormFieldMessageContainer>
                <Banner $type={'error' as BannerType}>
                  <Typography $variant="body-md-bold">
                    {errors.driverId.message}
                  </Typography>
                </Banner>
              </FormFieldMessageContainer>
            )}
            <CarFieldLabel $variant="body-md">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_label'})}</CarFieldLabel>
            <FormFieldControl>
              <Controller
                name='driverId'
                control={control}
                rules={{
                  required: intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_required_message'}),
                }}

                render={({ field }) => (
                  <RadioGroup.Root defaultValue={field.value} onValueChange={handleDriverChange}>
                    <SpaceBetween direction="y" spacing={space('primitives_space_8')}>
                      {availableDrivers?.map((availableDriver) => {
                        const isDisabled = driverId === availableDriver ? false : Boolean(carLists?.find((car) => car.driverId === availableDriver))

                        return <DriverFormOption key={availableDriver}
                                              disabled={isDisabled}>
                          <ListItem.Control>
                            <RadioGroup.Item value={availableDriver} disabled={isDisabled} />
                          </ListItem.Control>
                          <ListItem.Content>
                            <DriverFormLabel>{participants[availableDriver]?.name}</DriverFormLabel>
                          </ListItem.Content>
                        </DriverFormOption>
                      })}
                    </SpaceBetween>
                  </RadioGroup.Root>
                )}
              />
            </FormFieldControl>

            <CarFieldHint>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_hint'})}</CarFieldHint>
          </FormField>
          <FormField>
            <CarFieldLabel>{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate' })}</CarFieldLabel>
            <FormFieldControl>

              <TextField.Root error={Boolean(errors?.driverBirthdate)}>
                <TextField.Content>
                  <TextField.Input
                    type='date'
                    max={driverMaxBirthdate.toISOString().split('T')[0]}
                    min={driverMinBirthdate.toISOString().split('T')[0]}

                    {...register('driverBirthdate', {
                      required: intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_required_message' }),
                      onChange: (e) => {
                        setValue("driverBirthdate", e.target.value, { shouldValidate: true });
                      },
                      validate: {
                        validateAge: (value: unknown) => {
                          if (typeof value !== 'string') {
                            return 'Date not valid';
                          }
                          const isValidAge = validateMinAge(value)

                          if (isValidAge){
                            const driverBirthdateSelectedDate = new Date(value);
                            const isLegalAge = (driverBirthdateSelectedDate >= driverMaxAgeRentalConditions && driverBirthdateSelectedDate <= driverMinAgeRentalConditions)
                            setShowWarningDriverAge(!isLegalAge);
                          }

                          return isValidAge || intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_no_legal_age_message' }, { min: configurations?.minAgeDriver });
                        }
                      }
                    })}
                  />
                </TextField.Content>
              </TextField.Root>
            </FormFieldControl>
            <FormFieldMessageContainer>
              {errors?.driverBirthdate && (
                <FormFieldError>{errors?.driverBirthdate?.message}</FormFieldError>
              )}
              {!errors?.driverBirthdate && showWarningDriverAge && <Banner $type={'neutral-dark' as BannerType}>
                <Typography>
                  {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_birthdate_out_of_bounds_message'}, {
                    min: configurations?.rentalConditionsMinAgeDriver,
                    max: configurations?.rentalConditionsMaxAgeDriver
                  })}
                </Typography>
              </Banner>}
            </FormFieldMessageContainer>
          </FormField>

          <FormField
            $error={
              errors?.hasCreditCard
                ? Boolean(errors?.hasCreditCard)
                : undefined
            }
          >
            {errors.hasCreditCard && (
              <FormFieldMessageContainer>
                <Banner $type={'error' as BannerType}>
                  <Typography $variant="body-md-bold">
                    {errors.hasCreditCard.message}
                  </Typography>
                </Banner>
              </FormFieldMessageContainer>
            )}

            <FormFieldControl>
              <Controller
                name='hasCreditCard'
                control={control}
                rules={{
                  required: intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_credit_card_required_message'}),
                }}
                render={({ field }) => (
                  <DriverFormOption onClick={field.onChange}>
                    <ListItem.Control>
                      <CheckboxElement checked={field.value} onCheckedChange={field.onChange}/>
                    </ListItem.Control>
                    <ListItem.Content>
                      <DriverFormLabel>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_credit_card_label'})}</DriverFormLabel>
                    </ListItem.Content>
                  </DriverFormOption>
                )}
              />
            </FormFieldControl>
          </FormField>
        </SpaceBetween>
      </Form>
    </DriverFormWrapper>
    <SheetFooter $isFixed={isFixed}>
      <SpaceBetween direction="x" spacing={space('primitives_space_8')} justifyContent='end'>
        <Button $hierarchy="primary" $fullSize onClick={handleSubmit(onSubmit)}>
          {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_save_button_text'})}
        </Button>
      </SpaceBetween>
    </SheetFooter>
  </>
}

const Form = styled.form``;
const CarListName = styled(Typography)`
    color: ${tok('semantics_text_color')};
`
const CarFieldLabel = styled(Typography).attrs({
  $variant: 'body-md',
})`
  color: ${tok('semantics_text_color')};

  ${({ as }) =>
  as === 'label' &&
  `
    cursor: pointer;
  `}
`;
const CarFieldHint = styled(Typography).attrs({
  $variant: 'body-sm',
})`
  color: ${tok('semantics_text_color')};
  display: block;
`;
const DriverFormOption = styled(ListItem.Root)`
  padding: ${space('primitives_space_8')};
`
const DriverFormLabel = styled(ListItem.Label)`
  ${Typography.modVariant('body-md')}
  color: ${tok('semantics_text_color')};
`
const DriverFormWrapper = styled(SheetContentWrapper)`
  padding: ${space('primitives_space_16')};
`
